import { captureException, startSpan } from "@sentry/react";

import {
    ICommentResponse,
    ICommentsResponse,
    ICreateCommentRequest,
} from "../../types";
import {
    doDelete,
    doGet,
    doGetOptAuth,
    doPostJson,
    doPutJson,
} from "../core/http";

export const createComment = async (
    commentRequest: ICreateCommentRequest
): Promise<ICommentResponse> => {
    try {
        const response = await startSpan(
            { name: "createComment" },
            async () => {
                return doPostJson("/api/comments", true, commentRequest);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comment: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to create comment." };
    }
};

export const getCommentsByImage = async (
    imageId: string,
    sort: string = "top"
): Promise<ICommentsResponse> => {
    const url = `/api/comments/image/${imageId}?sort=${sort}`;
    try {
        const response = await startSpan(
            { name: "getCommentsByImage" },
            async () => {
                return doGetOptAuth(url);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comments: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to retrieve comments." };
    }
};

export const getReplies = async (
    parentCommentId: string,
    page: number,
    limit: number
): Promise<ICommentsResponse> => {
    const url = `/api/comments/${parentCommentId}/replies?page=${page}&size=${limit}`;
    try {
        const response = await startSpan({ name: "getReplies" }, async () => {
            return doGetOptAuth(url);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comments: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to retrieve replies." };
    }
};

export const likeComment = async (
    commentId: string
): Promise<ICommentResponse> => {
    const url = `/api/comments/${commentId}/like`;
    try {
        const response = await startSpan({ name: "likeComment" }, async () => {
            return doPostJson(url, true, {});
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comment: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to like comment." };
    }
};

export const getLikedComments = async (
    imageId: string
): Promise<ICommentsResponse> => {
    const url = `/api/comments/likes/image/${imageId}`;
    try {
        const response = await startSpan(
            { name: "getLikedComments" },
            async () => {
                return doGet(url, true);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comments: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to retrieve liked comments." };
    }
};

export const deleteComment = async (
    commentId: string
): Promise<{ error?: string }> => {
    const url = `/api/comments/${commentId}`;
    try {
        const { error } = await startSpan({ name: "deleteComment" }, () => {
            return doDelete(url, true);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to delete comment." };
        }

        return {};
    } catch (e) {
        captureException(e);
        return { error: "Failed to delete comment." };
    }
};

export const updateComment = async (
    commentId: string,
    newContent: string
): Promise<ICommentResponse> => {
    const url = `/api/comments/${commentId}`;
    try {
        const response = await startSpan(
            { name: "updateComment" },
            async () => {
                return doPutJson(url, true, { content: newContent });
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { comment: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to update comment." };
    }
};
